import { SnPoint, SnLonLat } from '@lib/geo/helpers';

import { Evented } from '../core';
import {
  toGoogleMapPoint,
  toGoogleMapLatLng,
  fromGoogleMapPoint,
  fromGoogleMapLatLng,
} from '../helpers';
import { GoogleMap } from '../map';
import { IMapBaseOverlayEvents } from './base-overlay-events';
import { IMapBaseOverlayOptions } from './base-overlay-options';

let uniqueId = 0;

const DEFAULT_OPTIONS: IMapBaseOverlayOptions = {
  zIndex: 0,
  opacity: 1,
  clickable: true,
};

export abstract class MapBaseOverlay<
  TEvents extends IMapBaseOverlayEvents = IMapBaseOverlayEvents,
> extends Evented<TEvents> {
  protected map: GoogleMap;

  protected readonly id: string;
  protected zIndex: number;
  protected opacity: number;
  protected clickable: boolean;
  protected updated: number = Date.now();

  private readonly _googleMapOverlay: google.maps.OverlayView;

  protected constructor(options?: IMapBaseOverlayOptions) {
    super();

    options = { ...DEFAULT_OPTIONS, ...options };

    this.id = options.id || `sn-map-overlay-${uniqueId++}`;
    this.zIndex = options.zIndex;
    this.opacity = options.opacity;
    this.clickable = options.clickable;

    this._googleMapOverlay = this._createGoogleMapOverlay();

    if (options.map) {
      this.addTo(options.map);
    }
  }

  public getUpdated(): number {
    return this.updated;
  }

  public wasUpdated(): void {
    this.updated = Date.now();
  }

  public getId(): string {
    return this.id;
  }

  public getZIndex(): number {
    return this.zIndex;
  }

  public setZIndex(zIndex: number): this {
    if (this.zIndex !== zIndex) {
      this.zIndex = zIndex;

      this.fire('zIndexChange', zIndex);
    }

    return this;
  }

  public getOpacity(): number {
    return this.opacity;
  }

  public setOpacity(opacity: number): this {
    if (this.opacity !== opacity) {
      this.opacity = opacity;

      this.fire('opacityChange', opacity);
    }

    return this;
  }

  public getClickable(): boolean {
    return this.clickable;
  }

  public setClickable(clickable: boolean): this {
    if (this.clickable !== clickable) {
      this.clickable = clickable;

      this.fire('clickableChange', clickable);
    }

    return this;
  }

  public getMap(): GoogleMap {
    return this.map;
  }

  public addTo(map: GoogleMap, force?: boolean): this {
    if (this.map !== map || force) {
      this.map = map;
      this.map.overlays.add(this);

      this._googleMapOverlay.setMap(map.mapInstance);

      this.fire('add');
    }

    return this;
  }

  public remove(): this {
    const map = this.map;

    if (map) {
      this.map.overlays.remove(this);
      this.map = null;

      this._googleMapOverlay.setMap(null);

      this.fire('remove');
    }

    return this;
  }

  protected abstract draw(): void;
  protected abstract onAdd(map: GoogleMap): void;
  protected abstract onRemove(): void;

  protected getPanes(): google.maps.MapPanes {
    return this._googleMapOverlay.getPanes();
  }

  protected fromLonLatToPoint(lonLat: SnLonLat): SnPoint {
    const projection = this._googleMapOverlay.getProjection();
    const googleLatLng = toGoogleMapLatLng(lonLat);
    const googlePoint = projection.fromLatLngToDivPixel(googleLatLng);

    return fromGoogleMapPoint(googlePoint);
  }

  protected fromPointToLonLat(point: SnPoint): SnLonLat {
    const projection = this._googleMapOverlay.getProjection();
    const googlePoint = toGoogleMapPoint(point);
    const googleLatLon = projection.fromDivPixelToLatLng(googlePoint);

    return fromGoogleMapLatLng(googleLatLon);
  }

  private _createGoogleMapOverlay(): google.maps.OverlayView {
    const googleOverlay = new google.maps.OverlayView();
    googleOverlay.onAdd = () => this.onAdd(this.map);
    googleOverlay.draw = () => this.draw();
    googleOverlay.onRemove = () => this.onRemove();

    return googleOverlay;
  }
}
